import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Segment, Divider, Button, Label, Icon } from 'semantic';
import { useTranslation } from 'react-i18next';
import { safeFileName } from 'utils/formatting';

import { FilterMapping } from 'components/search/Provider';

import AppWrapper from 'components/AppWrapper';
import Breadcrumbs from 'components/Breadcrumbs';
import { Search, SearchFilters, ListHeader, Layout } from 'components';
import { formatDateRangeChip } from 'utils/filters-header';

import { request } from 'utils/api';

import Table from './Table';
import { Group } from 'components/Layout/helpers';

type FilterFields = 'searchPhrase' | 'createdAt' | 'isPartnerProgramEligible';

export default function EvseControllers() {
  const { t } = useTranslation();

  function onDataNeeded(filters: any) {
    return request({
      method: 'POST',
      path: '/1/evse-controllers/maintenance/search/fast',
      body: {
        ...filters,
        // this indicates that we want to include child provider accounts in the search if user has enough permissions
        maybeIncludeChildProviderAccounts: true,
      },
    });
  }

  const filterMapping: FilterMapping<FilterFields> = {
    searchPhrase: {
      name: 'searchPhrase',
      type: 'string',
    },
    createdAt: {
      name: 'createdAt',
      type: 'date',
      range: true,
    },
    isPartnerProgramEligible: {
      name: 'isPartnerProgramEligible',
      type: 'boolean',
    },
  } as const;

  return (
    <AppWrapper>
      <Container>
        <Search.Provider
          onDataNeeded={onDataNeeded}
          filterMapping={filterMapping}>
          {({ filters, onFilterChange }) => {
            return (
              <>
                <Breadcrumbs
                  path={[
                    <Link key="maintaince" to="/maintenance/evse-controllers">
                      Maintenance
                    </Link>,
                  ]}
                  active={t(
                    'evseControllerMaintenance.title',
                    'EVSE Maintenance'
                  )}
                />

                <ListHeader
                  title={t(
                    'evseControllerMaintenance.title',
                    'EVSE Maintenance'
                  )}>
                  <Group position="right" spacing={10}>
                    <Search.ExportRange
                      onRangeSubmit={async (
                        from: Date,
                        to: Date,
                        filters: any
                      ) => {
                        await onDataNeeded({
                          format: 'csv',
                          limit: 100000,
                          createdAt: {
                            $gte: from,
                            $lte: to,
                          },
                          filename: safeFileName(
                            `evse-controllers-maintenance`
                          ),
                          ...filters,
                        });
                      }}
                    />
                    <Button
                      basic
                      as={Link}
                      to="/maintenance/evse-controllers/issues"
                      content="Detected Issues"
                      icon="crosshairs"
                    />
                  </Group>
                </ListHeader>

                <Segment>
                  <Layout horizontal spread stackable>
                    <SearchFilters.ModalFilterV2>
                      <SearchFilters.DateRange
                        name="createdAt"
                        label={t(
                          'evseControllerMaintenance.createdAt',
                          'Created At'
                        )}
                      />
                      {/* hide for now until we know if we want to show this here or not
                      <SearchFilters.Checkbox
                        name="isPartnerProgramEligible"
                        label={t(
                          'evseControllerMaintenance.isPartnerProgramEligible',
                          'Is Partner Program Eligible'
                        )}
                      />
                      */}
                    </SearchFilters.ModalFilterV2>

                    <Layout horizontal stackable center right>
                      <Search.Total />
                      <SearchFilters.Search
                        name="searchPhrase"
                        placeholder={t(
                          'evseControllerMaintenance.searchBy',
                          'Search by ID, Serial Number, Model Number, IMSI, etc'
                        )}
                      />
                    </Layout>
                  </Layout>
                  {Object.keys(filters).length > 0 && (
                    <>
                      <Divider
                        style={{ borderTop: '1px solid rgba(34, 36, 38, .15)' }}
                      />
                      {filters.createdAt && (
                        <Label
                          as="a"
                          onClick={() => {
                            onFilterChange({
                              name: filterMapping.createdAt.name,
                              value: undefined,
                            });
                          }}>
                          {t('common.createdAt', 'Created at')}:&nbsp;
                          {formatDateRangeChip(
                            filters.createdAt.$gte,
                            filters.createdAt.$lte
                          )}
                          <Icon name="delete" />
                        </Label>
                      )}
                      {filters.isPartnerProgramEligible && (
                        <Label
                          as="a"
                          onClick={() => {
                            onFilterChange({
                              name: filterMapping.isPartnerProgramEligible.name,
                              value: undefined,
                            });
                          }}>
                          {t(
                            'evseControllerMaintenance.isPartnerProgramEligible',
                            'Partner Program Eligible'
                          )}
                          <Icon name="delete" />
                        </Label>
                      )}
                    </>
                  )}
                </Segment>

                <Search.Status
                  noResults={t(
                    'evseControllerMaintenance.noEvseController',
                    'No EVSE Controllers assigned yet'
                  )}
                />
                <Table />
                <Divider hidden />
                <div style={{ textAlign: 'center' }}>
                  <Search.Pagination />
                </div>
              </>
            );
          }}
        </Search.Provider>
      </Container>
    </AppWrapper>
  );
}
